// Entity rendering (MOB-1 box rigs, MOB-5 arrows, PLR-5 XP orbs). Client-only
// (ENG-1). Pools Three objects by entity id; disposes them when the sim removes
// the entity. The sim owns all positions — this only visualises them, modulating
// brightness by the daylight factor so mobs darken at night with the world.
import * as THREE from 'three';
import { CREEPER } from '../../../sim/config';
import { scaleOf, type EntitySystem } from '../../../sim/entities/entity';
import type { MobDef } from '../../../sim/registry/schemas';
import { buildRig, disposeRig, type RigInstance } from './rig';
import { mendelianPhenotype } from '../../../sim/heredity/express';
import { makeNametag } from '../remoteplayers';
import type { Gene } from '../../../sim/heredity/genes';

import { tropicalTints } from '../../../sim/game/tropical_fish';

/** Horse-barding tint per tier: leather, iron, gold, diamond (M61). */
const BARDING_HEX = [0xb0825c, 0xd8d8de, 0xf4d654, 0x68dcd8];

/** Sheep wool tint per dye variant (DYE_ORDER index; 0 = white). */
const DYE_HEX = [
  0xeeeeee, 0xf07613, 0xbd44b3, 0x3aafd9, 0xf8c627, 0x70b919, 0xed8dac, 0x3e4447,
  0x8e8e86, 0x158991, 0x792aac, 0x35399d, 0x724728, 0x546d1b, 0xa02722, 0x141519,
] as const;

interface MobView {
  rig: RigInstance;
  walkPhase: number;
  /** The genome the rig's phenotype was last painted from (§28.6) — `''` for none, and
   *  undefined until the first paint. Tints and part visibility are applied when this
   *  CHANGES, never per frame. */
  genomeKey?: string;
  /** A named mob's floating name (§28.9) — the same canvas sprite remote players wear,
   *  kept OUT of the rig group so the rig's genetic scale does not resize the text. */
  nametag?: { text: string; sprite: THREE.Sprite; mat: THREE.SpriteMaterial; tex: THREE.CanvasTexture };
}

/** Paint a rig from its genome (§28.6): a `tint` gene colours its named parts by the
 *  expressed allele; a `part` gene shows or hides its part. With NO genome a tint gene
 *  leaves the rig's own colours alone, and a part gene's part is HIDDEN — so a rule-off
 *  chicken looks exactly as it did before the comb existed (D-TF-246). */
function paintPhenotype(rig: RigInstance, genes: readonly Gene[], genome: Uint8Array | undefined): void {
  for (const g of genes) {
    if (g.kind === 'mendelian' && g.expresses.part && !genome) {
      const mesh = rig.byName.get(g.expresses.part.name);
      if (mesh) mesh.visible = false;
    }
  }
  if (!genome) return;
  for (const { gene, allele } of mendelianPhenotype(genes, genome)) {
    const { tint, part } = gene.expresses;
    const hex = tint?.[allele];
    if (tint && typeof hex === 'string') {
      for (const name of tint.parts) {
        const mesh = rig.byName.get(name);
        if (!mesh) continue;
        for (const m of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
          (m as THREE.MeshLambertMaterial).color.set(hex);
        }
      }
    }
    if (part) {
      const mesh = rig.byName.get(part.name);
      if (mesh) mesh.visible = part[allele] === true;
    }
  }
}

export class EntityRenderer {
  private readonly group = new THREE.Group();
  private readonly mobs = new Map<number, MobView>();
  private readonly arrows = new Map<number, THREE.Mesh>();
  private readonly orbs = new Map<number, THREE.Mesh>();
  private readonly hemi = new THREE.HemisphereLight(0xffffff, 0x606060, 1);
  private readonly arrowGeo = new THREE.BoxGeometry(0.08, 0.08, 0.9);
  private readonly arrowMat = new THREE.MeshBasicMaterial({ color: 0x4a3826 });
  private readonly snowballGeo = new THREE.SphereGeometry(0.14, 6, 6);
  private readonly snowballMat = new THREE.MeshBasicMaterial({ color: 0xf2f7ff });
  private readonly orbGeo = new THREE.SphereGeometry(0.12, 6, 6);
  private readonly orbMat = new THREE.MeshBasicMaterial({ color: 0x8fe84a });

  constructor(scene: THREE.Scene, private readonly defs: ReadonlyMap<string, MobDef>) {
    scene.add(this.group);
    scene.add(this.hemi);
  }

  update(sys: EntitySystem, daylight: number): void {
    this.hemi.intensity = 0.35 + 0.75 * daylight;
    this.syncMobs(sys);
    this.syncArrows(sys);
    this.syncOrbs(sys);
  }

  /** A rendered part's colour, as `#rrggbb`, for an entity whose rig was painted from a
   *  genome (§28.16 `entityTint`) — null for no such entity, a species with no table, or
   *  an animal with no genome. */
  tintOf(id: number, part: string): string | null {
    const view = this.mobs.get(id);
    if (!view?.genomeKey) return null;
    const mesh = view.rig.byName.get(part);
    if (!mesh) return null;
    const m = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
    return `#${(m as THREE.MeshLambertMaterial).color.getHexString()}`;
  }

  /** The name a mob's nametag sprite shows (§28.9), or null when it wears none. */
  nametagOf(id: number): string | null {
    return this.mobs.get(id)?.nametag?.text ?? null;
  }

  /** Whether a rendered part is visible (the comb, §28.6), or null. */
  partVisible(id: number, part: string): boolean | null {
    return this.mobs.get(id)?.rig.byName.get(part)?.visible ?? null;
  }

  /** The rig's rendered scale (§28.16 `entityScale`), or null. */
  scaleOf(id: number): number | null {
    return this.mobs.get(id)?.rig.group.scale.x ?? null;
  }

  /** Drop every pooled rig/mesh (dimension switch, DIM-1) so ids from the new
   *  dimension don't reuse the old dimension's meshes. */
  clear(): void {
    for (const view of this.mobs.values()) {
      this.group.remove(view.rig.group); disposeRig(view.rig);
      if (view.nametag) { this.group.remove(view.nametag.sprite); view.nametag.mat.dispose(); view.nametag.tex.dispose(); }
    }
    for (const mesh of this.arrows.values()) this.group.remove(mesh);
    for (const mesh of this.orbs.values()) this.group.remove(mesh);
    this.mobs.clear();
    this.arrows.clear();
    this.orbs.clear();
  }

  private syncMobs(sys: EntitySystem): void {
    const seen = new Set<number>();
    for (const e of sys.entities) {
      seen.add(e.id);
      let view = this.mobs.get(e.id);
      if (!view) {
        const def = this.defs.get(e.type);
        if (!def) continue;
        view = { rig: buildRig(def), walkPhase: 0 };
        this.group.add(view.rig.group);
        this.mobs.set(e.id, view);
      }
      const def = this.defs.get(e.type)!;
      const g = view.rig.group;
      // A tamed pet ordered to sit hunkers down a little (visual cue).
      g.position.set(e.x, e.y - (e.sitting ? 0.22 : 0), e.z);
      g.rotation.y = e.yaw;
      // Base scale: babies are half-size, slimes scale by their size (variant).
      const baseScale = scaleOf(e, def);
      g.scale.setScalar(baseScale);

      // §28.9: a named mob wears its name at `height + 0.5` (the name never clears in v1).
      if (e.name !== undefined) {
        if (view.nametag?.text !== e.name) {
          if (view.nametag) { this.group.remove(view.nametag.sprite); view.nametag.mat.dispose(); view.nametag.tex.dispose(); }
          view.nametag = { text: e.name, ...makeNametag(e.name) };
          this.group.add(view.nametag.sprite);
        }
        view.nametag.sprite.position.set(e.x, e.y + def.height * baseScale + 0.5, e.z);
      }

      // Heredity (§28.6): repaint tints/parts only when the genome changes. Scale needs
      // nothing here — it is `traits.scale`, already inside `scaleOf`.
      if (def.genes) {
        const key = e.genome ? e.genome.join(',') : '';
        if (key !== view.genomeKey) {
          paintPhenotype(view.rig, def.genes, e.genome);
          view.genomeKey = key;
        }
      }

      // Tropical fish: one mob wearing 22 liveries — tint its rig from the variant.
      if (e.type === 'tropical_fish') {
        const t = tropicalTints(e.variant);
        for (const [part, hex] of [['body', t.body], ['stripe', t.stripe], ['tail', t.tail]] as const) {
          const mesh = view.rig.byName.get(part);
          if (mesh) (mesh.material as THREE.MeshLambertMaterial).color.setHex(hex);
        }
      }

      // Sheep: hide the wool overlay when sheared; tint it by dye variant.
      if (e.type === 'sheep') {
        const wool = view.rig.byName.get('wool');
        if (wool) {
          wool.visible = !e.sheared;
          (wool.material as THREE.MeshLambertMaterial).color.setHex(DYE_HEX[e.variant] ?? DYE_HEX[0]!);
        }
      }

      // A llama wears its carpet (M61) — hidden bare, tinted by the dye it was
      // dressed in. `decor` is the DYE_ORDER index plus one, so 0 reads as naked.
      if (e.type === 'llama') {
        const carpet = view.rig.byName.get('carpet');
        if (carpet) {
          const decor = (e.data.decor ?? 0) as number;
          carpet.visible = decor > 0;
          if (decor > 0) (carpet.material as THREE.MeshLambertMaterial).color.setHex(DYE_HEX[decor - 1] ?? DYE_HEX[0]!);
        }
      }

      // …and a horse wears its barding, tinted by tier so you can see at a glance
      // whether that is leather or diamond on it.
      if (e.type === 'horse') {
        const barding = view.rig.byName.get('barding');
        if (barding) {
          const tier = (e.data.barding ?? 0) as number;
          barding.visible = tier > 0;
          if (tier > 0) (barding.material as THREE.MeshLambertMaterial).color.setHex(BARDING_HEX[tier - 1] ?? BARDING_HEX[0]!);
        }
      }

      // Walk cycle: swing legs proportional to horizontal speed (sitting pets rest).
      const speed = e.sitting ? 0 : Math.hypot(e.vx, e.vz);
      view.walkPhase += speed * 6;
      const swing = Math.sin(view.walkPhase) * Math.min(0.7, speed * 6);
      view.rig.legs.forEach((leg, i) => { leg.rotation.x = i % 2 === 0 ? swing : -swing; });

      // Hurt flash (red) + creeper swell (flash white, grow) near detonation;
      // a charged creeper (variant 1) glows electric blue; a raid captain (a
      // banner-bearing variant-1 raider) glows ominous purple (RAID, M14.2).
      const swelling = e.type === 'creeper' && e.fuse > 0;
      const charged = e.type === 'creeper' && e.variant === 1;
      const captain = def.raider && e.variant === 1;
      const flash = e.hurtTimer > 0 ? 0x661111 : swelling && (e.fuse & 2) ? 0x888888 : charged ? 0x1a3a66 : captain ? 0x3a1a4a : 0x000000;
      for (const m of view.rig.materials) m.emissive.setHex(flash);
      if (swelling) g.scale.setScalar(baseScale * (1 + (e.fuse / CREEPER.FUSE_TICKS) * 0.3));
    }
    for (const [id, view] of this.mobs) {
      if (seen.has(id)) continue;
      this.group.remove(view.rig.group);
      disposeRig(view.rig);
      if (view.nametag) { this.group.remove(view.nametag.sprite); view.nametag.mat.dispose(); view.nametag.tex.dispose(); }
      this.mobs.delete(id);
    }
  }

  private syncArrows(sys: EntitySystem): void {
    const seen = new Set<number>();
    for (const p of sys.projectiles) {
      seen.add(p.id);
      let mesh = this.arrows.get(p.id);
      if (!mesh) {
        // Snowballs + firework rockets render as a small sphere; arrows as a shaft.
        mesh = p.type === 'snowball' || p.type === 'firework'
          ? new THREE.Mesh(this.snowballGeo, this.snowballMat)
          : new THREE.Mesh(this.arrowGeo, this.arrowMat);
        this.group.add(mesh); this.arrows.set(p.id, mesh);
      }
      mesh.position.set(p.x, p.y, p.z);
      if (!p.stuck && p.type !== 'snowball' && p.type !== 'firework') mesh.lookAt(p.x + p.vx, p.y + p.vy, p.z + p.vz);
    }
    for (const [id, mesh] of this.arrows) {
      if (seen.has(id)) continue;
      this.group.remove(mesh); this.arrows.delete(id);
    }
  }

  private syncOrbs(sys: EntitySystem): void {
    const seen = new Set<number>();
    const bob = Math.sin(performance.now() / 200) * 0.05;
    for (const o of sys.orbs) {
      seen.add(o.id);
      let mesh = this.orbs.get(o.id);
      if (!mesh) { mesh = new THREE.Mesh(this.orbGeo, this.orbMat); this.group.add(mesh); this.orbs.set(o.id, mesh); }
      mesh.position.set(o.x, o.y + 0.2 + bob, o.z);
    }
    for (const [id, mesh] of this.orbs) {
      if (seen.has(id)) continue;
      this.group.remove(mesh); this.orbs.delete(id);
    }
  }
}
